import { CpmButton } from '../../../components/design-system';
import { formatCpmCount } from '../../../constants/centralProductMaster.constants';
import type { CpmOpenBatchJob, CpmStatsResponse } from '../../../types/centralProductMaster.types';
import SectionError from './SectionError';

interface BatchJobHealthPanelProps {
    batchJobHealth: CpmStatsResponse['batchJobHealth'];
    openBatchJobs: CpmOpenBatchJob[];
    confirmingJobId: string | null;
    retriedJobIds: string[];
    onAskRetry: (id: string) => void;
    onCancelRetry: () => void;
    onConfirmRetry: (id: string) => void;
    retrying: boolean;
    error?: boolean;
    onRetryLoad?: () => void;
    retryingLoad?: boolean;
}

function jobKind(job: CpmOpenBatchJob): 'failed' | 'stuck' | 'infra' {
    if (job.status === 'FAILED') return 'failed';
    if (job.status === 'COMPLETE' && (job.infraFailedCount ?? 0) > 0) return 'infra';
    return 'stuck';
}

function relativeWhen(iso?: string | null): string {
    if (!iso) return '—';
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms)) return '—';
    const hours = Math.floor(ms / 3_600_000);
    if (hours < 24) return hours <= 0 ? 'Just now' : `${hours} hours ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
}

export default function BatchJobHealthPanel({
    batchJobHealth,
    openBatchJobs,
    confirmingJobId,
    retriedJobIds,
    onAskRetry,
    onCancelRetry,
    onConfirmRetry,
    retrying,
    error = false,
    onRetryLoad,
    retryingLoad = false,
}: BatchJobHealthPanelProps) {
    const problemJobs = openBatchJobs.filter((j) => !retriedJobIds.includes(j._id));

    return (
        <div
            id="batch-jobs"
            className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] rounded-[16px] shadow-[var(--cpm-shadow-card)] flex flex-col"
            style={{ padding: 20, gap: 16 }}
        >
            <div className="flex items-baseline" style={{ gap: 10 }}>
                <h2 className="m-0 !text-[14px] !font-extrabold !text-[var(--cpm-text-primary)]">
                    Batch job health
                </h2>
                <span
                    className="ml-auto text-[var(--cpm-text-secondary)]"
                    style={{ fontSize: 12, fontWeight: 600 }}
                >
                    Daily linking runs, last 30 days
                </span>
            </div>

            {error ? (
                <SectionError
                    title="Couldn't load batch job health"
                    body="Run history is unavailable, so failed and stuck runs can't be shown or retried right now."
                    onRetry={onRetryLoad}
                    retrying={retryingLoad}
                />
            ) : null}

            {!error && problemJobs.length > 0 ? (
                <span
                    className="text-[var(--cpm-shortfall-text-med)]"
                    style={{ fontSize: 12.5, fontWeight: 800, marginTop: -8 }}
                >
                    {problemJobs.length === 1
                        ? '1 run needs attention'
                        : `${problemJobs.length} runs need attention`}
                </span>
            ) : null}
            {!error && problemJobs.length === 0 ? (
                <div
                    className="flex items-center rounded-[10px] bg-[var(--cpm-success-bg)] border border-[var(--cpm-success-border)]"
                    style={{ gap: 9, padding: '12px 14px' }}
                >
                    <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--cpm-success-dot)"
                        strokeWidth="2"
                        className="shrink-0"
                    >
                        <path d="M20 6 9 17l-5-5" />
                    </svg>
                    <span
                        className="text-[var(--cpm-success-text)]"
                        style={{ fontSize: 13, fontWeight: 700 }}
                    >
                        No failed or stuck jobs right now.
                    </span>
                </div>
            ) : null}

            {!error ? (
                <>
                    <div className="flex flex-col" style={{ gap: 12 }}>
                        {openBatchJobs.map((job) => {
                            const retried = retriedJobIds.includes(job._id);
                            const kind = jobKind(job);
                            const confirming = confirmingJobId === job._id;
                            const tone =
                                retried || kind === 'stuck' || kind === 'infra'
                                    ? {
                                          bg: retried
                                              ? 'var(--cpm-surface-subtle)'
                                              : 'var(--cpm-warning-bg)',
                                          border: retried
                                              ? 'var(--cpm-border-default)'
                                              : 'var(--cpm-warning-border)',
                                          icon: retried
                                              ? 'var(--cpm-text-secondary)'
                                              : 'var(--cpm-warning-icon)',
                                          text: retried
                                              ? 'var(--cpm-text-primary)'
                                              : 'var(--cpm-warning-text)',
                                          textMed: retried
                                              ? 'var(--cpm-text-secondary)'
                                              : 'var(--cpm-warning-text-med)',
                                      }
                                    : {
                                          bg: 'var(--cpm-shortfall-bg)',
                                          border: 'var(--cpm-shortfall-border)',
                                          icon: 'var(--cpm-shortfall-icon)',
                                          text: 'var(--cpm-shortfall-text)',
                                          textMed: 'var(--cpm-shortfall-text-med)',
                                      };

                            const statusLabel = retried
                                ? 'Requeued'
                                : kind === 'failed'
                                  ? 'Failed'
                                  : kind === 'infra'
                                    ? 'Complete — infra failures left over'
                                    : 'Stuck — no completion';
                            const statusCode = retried
                                ? 'REQUEUED'
                                : kind === 'failed'
                                  ? 'FAILED'
                                  : kind === 'infra'
                                    ? 'COMPLETE'
                                    : 'STUCK';
                            const items =
                                kind === 'infra'
                                    ? formatCpmCount(job.infraFailedCount ?? 0)
                                    : formatCpmCount(job.itemCount ?? 0);
                            const itemsLabel = retried
                                ? 'Items requeued'
                                : kind === 'infra'
                                  ? 'Infra-failed items'
                                  : kind === 'failed'
                                    ? 'Items never applied'
                                    : 'Items still waiting';
                            const reason = retried
                                ? 'Run rejected and its unresolved items requeued — the next daily linking run will pick them up fresh.'
                                : job.failureReason ||
                                  (kind === 'infra'
                                      ? 'This run finished adjudicating, but some items hit repeated infrastructure errors and never reached a final decision.'
                                      : 'This run has been open without finishing.');

                            return (
                                <div
                                    key={job._id}
                                    className="rounded-[10px] flex flex-col"
                                    style={{
                                        padding: 16,
                                        gap: 12,
                                        background: tone.bg,
                                        border: `1px solid ${tone.border}`,
                                    }}
                                >
                                    <div className="flex items-center" style={{ gap: 10 }}>
                                        <span
                                            className="shrink-0 flex"
                                            style={{ color: tone.icon }}
                                        >
                                            {retried ? (
                                                <svg
                                                    width="17"
                                                    height="17"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                >
                                                    <path d="M3 12a9 9 0 1 0 9-9 9 9 0 0 0-6.36 2.64L3 8" />
                                                    <path d="M3 3v5h5" />
                                                </svg>
                                            ) : kind === 'failed' ? (
                                                <svg
                                                    width="17"
                                                    height="17"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                >
                                                    <circle cx="12" cy="12" r="10" />
                                                    <path d="m15 9-6 6" />
                                                    <path d="m9 9 6 6" />
                                                </svg>
                                            ) : (
                                                <svg
                                                    width="17"
                                                    height="17"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                >
                                                    <circle cx="12" cy="12" r="10" />
                                                    <path d="M12 6v6l4 2" />
                                                </svg>
                                            )}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: 13.5,
                                                fontWeight: 800,
                                                color: tone.text,
                                            }}
                                        >
                                            {statusLabel}
                                        </span>
                                        <span
                                            className="font-[family-name:var(--cpm-font-mono)]"
                                            style={{
                                                fontSize: 10,
                                                fontWeight: 700,
                                                letterSpacing: '0.04em',
                                                color: tone.textMed,
                                            }}
                                        >
                                            {statusCode}
                                        </span>
                                        <span
                                            className="ml-auto font-[family-name:var(--cpm-font-mono)] text-[var(--cpm-text-tertiary)]"
                                            style={{
                                                fontSize: 11,
                                                fontWeight: 700,
                                                letterSpacing: '0.04em',
                                            }}
                                        >
                                            {String(job._id).slice(-8).toUpperCase()}
                                        </span>
                                    </div>
                                    <div
                                        className="flex rounded-[8px] bg-white"
                                        style={{
                                            gap: 20,
                                            padding: '10px 12px',
                                            border: `1px solid ${tone.border}`,
                                        }}
                                    >
                                        <div className="flex flex-col" style={{ gap: 3 }}>
                                            <span
                                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                                style={{
                                                    fontSize: 10,
                                                    fontWeight: 800,
                                                    letterSpacing: '0.04em',
                                                }}
                                            >
                                                {retried
                                                    ? 'Originally'
                                                    : kind === 'stuck'
                                                      ? 'Started'
                                                      : 'When'}
                                            </span>
                                            <span style={{ fontSize: 13.5, fontWeight: 800 }}>
                                                {relativeWhen(
                                                    kind === 'stuck'
                                                        ? job.submittedAt
                                                        : (job.completedAt ??
                                                              job.updatedAt ??
                                                              job.submittedAt)
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex flex-col" style={{ gap: 3 }}>
                                            <span
                                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                                style={{
                                                    fontSize: 10,
                                                    fontWeight: 800,
                                                    letterSpacing: '0.04em',
                                                }}
                                            >
                                                {itemsLabel}
                                            </span>
                                            <span
                                                className="tabular"
                                                style={{
                                                    fontSize: 13.5,
                                                    fontWeight: 800,
                                                    color: tone.textMed,
                                                }}
                                            >
                                                {items}
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                            lineHeight: 1.5,
                                            color: tone.textMed,
                                        }}
                                    >
                                        {reason}
                                    </div>
                                    {!retried && !confirming ? (
                                        <div>
                                            <CpmButton
                                                size="sm"
                                                variant="outline"
                                                onClick={() => onAskRetry(job._id)}
                                            >
                                                Retry this job
                                            </CpmButton>
                                        </div>
                                    ) : null}
                                    {confirming ? (
                                        <div
                                            className="rounded-[8px] bg-white border-[1.5px] border-[var(--cpm-brand-purple)] flex flex-col"
                                            style={{ padding: '13px 14px', gap: 11 }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: 12.5,
                                                    fontWeight: 600,
                                                    lineHeight: 1.45,
                                                }}
                                            >
                                                This will reject the run and requeue its unresolved
                                                items so they&apos;re picked up fresh by the next
                                                daily linking run — it won&apos;t re-process this
                                                run as-is.
                                            </span>
                                            <div className="flex" style={{ gap: 9 }}>
                                                <CpmButton
                                                    size="sm"
                                                    variant="navy"
                                                    isLoading={retrying}
                                                    onClick={() => onConfirmRetry(job._id)}
                                                >
                                                    Requeue items
                                                </CpmButton>
                                                <CpmButton
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={onCancelRetry}
                                                >
                                                    Cancel
                                                </CpmButton>
                                            </div>
                                        </div>
                                    ) : null}
                                    {retried ? (
                                        <div
                                            className="flex items-center text-[var(--cpm-success-text)]"
                                            style={{ gap: 8, fontSize: 12.5, fontWeight: 700 }}
                                        >
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            >
                                                <path d="M20 6 9 17l-5-5" />
                                            </svg>
                                            Dismissed from needs-attention — kept here for history.
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex flex-col">
                        {[
                            {
                                label: 'Running',
                                code: 'RUNNING',
                                count: batchJobHealth.running,
                                hint: "Today's runs currently processing",
                                live: true,
                            },
                            {
                                label: 'Queued, not yet started',
                                code: 'QUEUED',
                                count: batchJobHealth.submittedNotStarted,
                                hint:
                                    batchJobHealth.submittedNotStarted === 0
                                        ? 'Nothing queued behind the current run'
                                        : 'Waiting to start',
                                muted: true,
                            },
                            {
                                label: 'Completed',
                                code: 'COMPLETE',
                                count: batchJobHealth.completedLast30Days,
                                hint: 'In the last 30 days',
                                live: true,
                            },
                        ].map((row) => (
                            <div
                                key={row.code}
                                className="flex items-center border-t border-[var(--cpm-border-default)]"
                                style={{ gap: 12, padding: '13px 2px' }}
                            >
                                <span
                                    className="shrink-0 rounded-full"
                                    style={{
                                        width: 9,
                                        height: 9,
                                        background: row.muted
                                            ? 'var(--cpm-text-tertiary)'
                                            : 'var(--cpm-success-dot)',
                                        boxShadow: row.live
                                            ? '0 0 0 3px var(--cpm-success-bg)'
                                            : undefined,
                                    }}
                                />
                                <div className="flex flex-col flex-1 min-w-0" style={{ gap: 3 }}>
                                    <span
                                        style={{
                                            fontSize: 13.5,
                                            fontWeight: 700,
                                            color: row.muted
                                                ? 'var(--cpm-text-secondary)'
                                                : undefined,
                                        }}
                                    >
                                        {row.label}
                                    </span>
                                    <span
                                        className="text-[var(--cpm-text-secondary)]"
                                        style={{ fontSize: 12, fontWeight: 600 }}
                                    >
                                        {row.hint}
                                    </span>
                                </div>
                                <span
                                    className="font-[family-name:var(--cpm-font-mono)] text-[var(--cpm-text-tertiary)]"
                                    style={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        letterSpacing: '0.04em',
                                    }}
                                >
                                    {row.code}
                                </span>
                                <span
                                    className="tabular"
                                    style={{
                                        width: 56,
                                        textAlign: 'right',
                                        fontSize: 19,
                                        fontWeight: 800,
                                        color: row.muted ? 'var(--cpm-text-secondary)' : undefined,
                                    }}
                                >
                                    {formatCpmCount(row.count)}
                                </span>
                            </div>
                        ))}
                    </div>
                </>
            ) : null}
        </div>
    );
}
